import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Empty, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { saveAs } from 'file-saver';

import { BreadcrumbContainer } from 'components/Breadcrumb';
import { Heading1 } from 'components/Heading1';
import { PageHeader } from 'components/PageHeader';
import { PageWrapper } from 'components/PageWrapper';
import { Table } from 'components/Table';
import { TextM } from 'components/Text';

import { useBreadcrumb } from 'modules/core/context/AppContext';
import { EscrowRoutes } from 'modules/escrow/constants/routes';
import { ImportsService } from 'modules/escrow/services';
import {
	getAccountRouteTypeIcon,
	getAccountRouteTypeName,
	useIsEscrowAccountRoute,
} from 'modules/escrow/utils/adminRoute';
import { ApiError } from 'types/ApiError';

import {
	DESTINATION_PAGE_TITLE,
	DestinationDownloadActions,
	DestinationFooterLeft,
	DestinationImportTable,
} from './components/DestinationImportTable';
import {
	TRANSFER_PAGE_TITLE,
	TransferDownloadActions,
	TransferFooterLeft,
	TransferImportTable,
} from './components/TransferImportTable';
import { ImportRecordStatus, ImportType } from './types';
import { Footer, FooterActions, Tabs } from './styles';

interface ErrorRow {
	line: number;
	description: string;
}

const EscrowImportsDetails = () => {
	const navigate = useNavigate();
	const { importId } = useParams<{ importId: string }>();
	const [api, contextHolder] = notification.useNotification();
	const { setBreadcrumb } = useBreadcrumb();
	const isEscrowRoute = useIsEscrowAccountRoute();

	const redirectAccount = isEscrowRoute
		? EscrowRoutes.ESCROW_BASE
		: EscrowRoutes.ACCOUNT_BASE;

	const accountBreadcrumbTitle = (
		<BreadcrumbContainer>
			{getAccountRouteTypeIcon()}
			<span>{getAccountRouteTypeName()}</span>
		</BreadcrumbContainer>
	);

	const { data, isLoading, error } = useQuery({
		queryKey: ['importDetails', importId],
		queryFn: () => ImportsService.getImport(importId as string),
		enabled: !!importId,
		retry: false,
	});

	const isNotFound = (error as ApiError | null)?.status === 404;
	const hasError = !!error;
	const isTransfer = data?.type === ImportType.TRANSFER;
	const pageTitle = isTransfer ? TRANSFER_PAGE_TITLE : DESTINATION_PAGE_TITLE;

	useEffect(() => {
		setBreadcrumb([
			{
				href: redirectAccount,
				title: accountBreadcrumbTitle,
			},
			{
				title: pageTitle,
			},
		]);
	}, [pageTitle]);

	const errorRows = (data?.records ?? []).reduce<ErrorRow[]>(
		(acc, record, index) => {
			if (record.status === ImportRecordStatus.ERROR) {
				acc.push({
					line: index + 2,
					description: record.error ?? '-',
				});
			}
			return acc;
		},
		[],
	);

	const errorColumns: ColumnsType<ErrorRow> = [
		{
			title: 'Linha',
			key: 'line',
			width: 120,
			render: (record: ErrorRow) => (
				<TextM weight="normal">{record.line}</TextM>
			),
		},
		{
			title: 'Descrição do erro',
			key: 'description',
			render: (record: ErrorRow) => (
				<TextM weight="normal">{record.description}</TextM>
			),
		},
	];

	const handleBack = () => {
		navigate(-1);
	};

	const handleDownloadRemessa = async () => {
		if (!importId) return;
		try {
			const { url } = await ImportsService.getTransferFile(importId);
			saveAs(url);
		} catch {
			api.error({ message: 'Erro ao baixar arquivo de remessa' });
		}
	};

	const handleDownloadRetorno = async () => {
		if (!importId) return;
		try {
			const { url } = await ImportsService.getReturnFile(importId);
			saveAs(url);
		} catch {
			api.error({ message: 'Erro ao baixar arquivo de retorno' });
		}
	};

	const renderPrimaryTable = () => {
		if (data?.type === ImportType.TRANSFER) {
			return (
				<TransferImportTable
					records={data.records}
					isLoading={isLoading}
				/>
			);
		}

		return (
			<DestinationImportTable
				records={data?.records ?? []}
				isLoading={isLoading}
			/>
		);
	};

	const renderDownloadActions = () => {
		if (isTransfer) {
			return (
				<TransferDownloadActions onDownload={handleDownloadRetorno} />
			);
		}

		return (
			<DestinationDownloadActions
				onDownloadRemessa={handleDownloadRemessa}
				onDownloadRetorno={handleDownloadRetorno}
			/>
		);
	};

	const renderFooterLeft = () => {
		if (data?.type === ImportType.TRANSFER) {
			return <TransferFooterLeft records={data.records} />;
		}

		return <DestinationFooterLeft records={data?.records ?? []} />;
	};

	const totalRecords = data?.records.length ?? 0;

	return (
		<>
			{contextHolder}
			<PageHeader isFluid={true}>
				<Heading1>{pageTitle}</Heading1>
			</PageHeader>
			<PageWrapper isFluid={true}>
				{hasError ? (
					<Empty
						description={
							isNotFound
								? 'Importação não encontrada'
								: 'Não foi possível carregar a importação. Tente novamente.'
						}
					/>
				) : (
					<Tabs
						defaultActiveKey="all"
						items={[
							{
								key: 'all',
								label: `Todas as contas (${totalRecords})`,
								children: renderPrimaryTable(),
							},
							{
								key: 'errors',
								label: `Erros (${errorRows.length})`,
								children: (
									<Table
										rowKey={(record: ErrorRow) =>
											String(record.line)
										}
										columns={errorColumns}
										dataSource={errorRows}
										loading={isLoading}
										scroll={{ y: 420 }}
										pagination={false}
									/>
								),
							},
						]}
					/>
				)}
				<Footer>
					{!hasError && renderFooterLeft()}
					<FooterActions>
						<Button type="link" onClick={handleBack}>
							Voltar
						</Button>
						{!hasError && renderDownloadActions()}
					</FooterActions>
				</Footer>
			</PageWrapper>
		</>
	);
};

export default EscrowImportsDetails;
